import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SpinWheelComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { ToggleSwitch } from '../../Common/ToggleSwitch';
import { Plus, Trash2 } from 'lucide-react';

interface SpinWheelSettingsProps {
  component: SpinWheelComponent;
}

const MODE_OPTIONS = [
  { value: 'freeSpin', key: 'freeSpin' },
  { value: 'codeToSpin', key: 'codeToSpin' },
] as const;

const STYLE_OPTIONS = [
  { value: 'default', key: 'default' },
  { value: 'modern', key: 'modern' },
  { value: 'neon', key: 'neon' },
  { value: 'minimal', key: 'minimal' },
] as const;

export const SpinWheelSettings: React.FC<SpinWheelSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();

  const handleUpdate = (updates: Partial<SpinWheelComponent['settings']>) => {
    updateComponent(component.id, { settings: { ...component.settings, ...updates } });
  };

  const addPrize = () => {
    const newPrize = {
      id: Date.now().toString(),
      label: t('settings.spinWheel.defaultPrizeName', { n: component.settings.prizes.length + 1 }),
      color: '#' + Math.floor(Math.random() * 16777215).toString(16),
      probability: 10,
      code: '',
    };
    handleUpdate({ prizes: [...component.settings.prizes, newPrize] });
  };

  const updatePrize = (prizeId: string, updates: Partial<typeof component.settings.prizes[0]>) => {
    const updatedPrizes = component.settings.prizes.map(prize =>
      prize.id === prizeId ? { ...prize, ...updates } : prize
    );
    handleUpdate({ prizes: updatedPrizes });
  };

  const removePrize = (prizeId: string) => {
    handleUpdate({ prizes: component.settings.prizes.filter(p => p.id !== prizeId) });
  };

  const totalProbability = component.settings.prizes.reduce((sum, p) => sum + p.probability, 0);

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.spinWheel.mode')}</label>
        <select
          value={component.settings.mode}
          onChange={(e) => handleUpdate({ mode: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {MODE_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>
              {t(`settings.spinWheel.modes.${m.key}` as const)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.spinWheel.wheelStyle')}</label>
        <select
          value={component.settings.style}
          onChange={(e) => handleUpdate({ style: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {STYLE_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {t(`settings.spinWheel.styles.${s.key}` as const)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.title')}</label>
        <input
          type="text"
          value={component.settings.title}
          onChange={(e) => handleUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.spinWheel.titlePlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.spinWheel.subtitle')}</label>
        <input
          type="text"
          value={component.settings.subtitle}
          onChange={(e) => handleUpdate({ subtitle: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.spinWheel.subtitlePlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.spinWheel.buttonText')}</label>
        <input
          type="text"
          value={component.settings.buttonText}
          onChange={(e) => handleUpdate({ buttonText: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.spinWheel.buttonTextPlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.spinWheel.winMessage')}</label>
        <textarea
          value={component.settings.winMessage}
          onChange={(e) => handleUpdate({ winMessage: e.target.value })}
          rows={2}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          placeholder={t('settings.spinWheel.winMessagePlaceholder')}
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('settings.spinWheel.winMessageHint', { prize: '{prize}', code: '{code}' })}
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium">
            {t('settings.spinWheel.prizesCount', { count: component.settings.prizes.length })}
          </label>
          <button
            onClick={addPrize}
            className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors"
          >
            <Plus size={14} />
            {t('settings.spinWheel.addPrize')}
          </button>
        </div>

        {totalProbability !== 100 && (
          <div className="mb-3 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
            {t('settings.spinWheel.totalProbabilityWarning', { total: totalProbability })}
          </div>
        )}

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {component.settings.prizes.map((prize, index) => (
            <div key={prize.id} className="p-3 bg-gray-700 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-300">
                  {t('settings.spinWheel.prizeNumber', { n: index + 1 })}
                </span>
                {component.settings.prizes.length > 1 && (
                  <button
                    onClick={() => removePrize(prize.id)}
                    className="p-1 hover:bg-gray-600 rounded transition-colors"
                    title={t('settings.spinWheel.removePrize')}
                  >
                    <Trash2 size={14} className="text-red-400" />
                  </button>
                )}
              </div>

              <input
                type="text"
                value={prize.label}
                onChange={(e) => updatePrize(prize.id, { label: e.target.value })}
                placeholder={t('settings.spinWheel.prizeNamePlaceholder')}
                className="w-full px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />

              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('settings.spinWheel.color')}</label>
                <ColorPicker
                  color={prize.color}
                  onChange={(color) => updatePrize(prize.id, { color })}
                />
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t('settings.spinWheel.probabilityLabel', { value: prize.probability })}
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={prize.probability}
                  onChange={(e) => updatePrize(prize.id, { probability: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>

              <input
                type="text"
                value={prize.code || ''}
                onChange={(e) => updatePrize(prize.id, { code: e.target.value })}
                placeholder={t('settings.spinWheel.prizeCodePlaceholder')}
                className="w-full px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-3">{t('settings.spinWheel.contactForm')}</label>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-300">{t('settings.spinWheel.requireContactInfo')}</span>
            <ToggleSwitch
              enabled={component.settings.requireContact}
              onChange={(enabled) => handleUpdate({ requireContact: enabled })}
            />
          </div>

          {component.settings.requireContact && (
            <>
              <div className="flex items-center justify-between pl-4">
                <span className="text-sm text-gray-400">{t('settings.spinWheel.requirePhone')}</span>
                <ToggleSwitch
                  enabled={component.settings.requirePhone}
                  onChange={(enabled) => handleUpdate({ requirePhone: enabled })}
                />
              </div>

              <div className="flex items-center justify-between pl-4">
                <span className="text-sm text-gray-400">{t('settings.spinWheel.requireEmail')}</span>
                <ToggleSwitch
                  enabled={component.settings.requireEmail}
                  onChange={(enabled) => handleUpdate({ requireEmail: enabled })}
                />
              </div>

              <div className="flex items-center justify-between pl-4">
                <span className="text-sm text-gray-400">{t('settings.spinWheel.requireName')}</span>
                <ToggleSwitch
                  enabled={component.settings.requireName}
                  onChange={(enabled) => handleUpdate({ requireName: enabled })}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-3">{t('settings.spinWheel.spinLimits')}</label>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">
              {t('settings.spinWheel.maxSpinsPerIp')}
            </label>
            <input
              type="number"
              min="0"
              value={component.settings.maxSpinsPerIP}
              onChange={(e) => handleUpdate({ maxSpinsPerIP: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">
              {t('settings.spinWheel.maxSpinsPerPhone')}
            </label>
            <input
              type="number"
              min="0"
              value={component.settings.maxSpinsPerPhone}
              onChange={(e) => handleUpdate({ maxSpinsPerPhone: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.spinWheel.wheelSizeLabel', { value: component.settings.wheelSize })}
        </label>
        <input
          type="range"
          min="200"
          max="400"
          step="10"
          value={component.settings.wheelSize}
          onChange={(e) => handleUpdate({ wheelSize: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.spinWheel.spinDurationLabel', { value: component.settings.spinDuration / 1000 })}
        </label>
        <input
          type="range"
          min="2000"
          max="8000"
          step="500"
          value={component.settings.spinDuration}
          onChange={(e) => handleUpdate({ spinDuration: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.textColor')}</label>
        <ColorPicker
          color={component.settings.textColor}
          onChange={(color) => handleUpdate({ textColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.accentColor')}</label>
        <ColorPicker
          color={component.settings.accentColor}
          onChange={(color) => handleUpdate({ accentColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.common.paddingPx', { value: component.settings.padding })}
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
    </div>
  );
};
